<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <b-link class="brand-logo">
        <logo />
      </b-link>

      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Login V2"
          />
        </div>
      </b-col>

      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            class="mb-1 font-weight-bold"
            title-tag="h2"
          >
            Welcome to Vatika ERP! 👋
          </b-card-title>
          <b-card-text class="mb-2">
            Please sign-in to your account and start the adventure
          </b-card-text>

          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div class="alert-body">
              <p>
                {{ errorMessage }}
              </p>
            </div>
          </b-alert>

          <validation-observer
            ref="loginForm"
            #default="{ validated, invalid }"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >

              <b-form-group
                label="Username"
                label-for="login-username"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Username"
                  vid="username"
                  rules="required"
                  mode="eager"
                >
                  <b-form-input
                    id="login-username"
                    v-model="username"
                    :state="errors.length > 0 ? false:null"
                    name="username"
                    placeholder="Username"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Password</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  vid="password"
                  rules="required"
                  mode="eager"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Password"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="loading || (validated && invalid)"
              >
                Sign in <b-spinner
                  v-if="loading"
                  small
                />
              </b-button>
            </b-form>
          </validation-observer>

        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BCardText, BCardTitle, BImg, BForm, BButton, BAlert, VBTooltip, BSpinner,
} from 'bootstrap-vue'
// eslint-disable-next-line no-unused-vars
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store/index'
import Logo from '@/layouts/components/Logo.vue'

import axios from 'axios'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BAlert,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    Logo,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      username: '',
      password: '',
      sideImg: require('@/assets/images/pages/login-v2.svg'),
      loading: false,
      errorMessage: null,
      forceLogin: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          this.loading = true
          const loginData = {
            username: this.username,
            password: this.password,
            from_erp: true,
            force_login: this.forceLogin, // Add force_login parameter
          }

          axios.post('/auth/login', loginData)
            .then(res => {
              const { data } = res

              const authData = {
                token: data.token,
                userName: data.user.username,
                isSuperUser: data.user.is_superuser,
                groups: data.user.groups,
              }

              // Set auth data first, then navigate
              this.$store.dispatch('auth/setAuthData', authData).then(() => {
                this.loading = false
                this.errorMessage = null
                this.forceLogin = false // Reset force login flag

                // Handle navigation after authentication is set
                this.$nextTick(() => {
                  const redirectPath = this.$route.query.redirect || null
                  if (redirectPath && !redirectPath.startsWith('/login')) {
                  // Only redirect if it's not a login path
                    this.$router.replace(redirectPath)
                  } else {
                  // Use replace instead of push to avoid navigation history issues
                    this.$router.replace({ name: 'home' })
                  }
                })
              }).catch(err => {
                this.loading = false
                this.errorMessage = err ? err.message : 'Something went wrong'
              })
            })
            .catch(error => {
              this.loading = false
              this.errorMessage = error ? error.message : 'Something went wrong'

              // Handle 409 Conflict status
              if (error.response && error.response.status === 409) {
                const message = error.response.data?.message || 'User is already logged in from another session. Do you want to force login?'
                this.$bvModal.msgBoxConfirm(message, {
                  title: 'Confirm Force Login',
                  okVariant: 'primary',
                  okTitle: 'OK',
                  cancelTitle: 'Cancel',
                  centered: true,
                })
                  .then(value => {
                    if (value) {
                    // User clicked OK, set forceLogin to true and call login again
                      this.forceLogin = true
                      this.login()
                    }
                  // If user clicked Cancel, do nothing
                  })
                  .catch(err => {
                    this.errorMessage = err ? err.message : 'Error with confirmation dialog:'
                  })
              } else {
              // Handle other errors
                const errors = error?.response?.data?.non_field_errors
                this.errorMessage = errors ? errors[0] : 'Something went wrong'
              }
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
